import { useBalanceStore } from '@/entities/balance';
import { useClientStorageFlag } from '@/entities/clientStorage';
import { useRecordingEntitlement } from '@/entities/user/currentUser';

import { IS_WHITE_LABEL } from '@/shared/environments';

import { RECORDING_INTRO_STORAGE_KEY } from '../lib/constants';
import { getRecordingIntroVariant, RecordingIntroVariant } from '../lib/getRecordingIntroVariant';

/**
 * The one-time intro a new recording should show before the recorder (trial and paid spaces, once per user), or null
 * to open the recorder directly. Only a user who may get an intro reads the once-per-user flag. While the plan or the
 * flag is still loading, or the flag failed to load, the answer is null: the recorder opens rather than risk showing
 * the intro twice.
 */
export const useRecordingIntroGate = (): null | RecordingIntroVariant => {
  const hasTrialPlan = useBalanceStore((state) => state.hasTrialPlan);
  const isBalanceLoaded = useBalanceStore((state) => state.isBalanceLoaded);
  const entitlement = useRecordingEntitlement();
  const isPlanKnown = isBalanceLoaded && entitlement.status !== 'loading';
  const variant = isPlanKnown
    ? getRecordingIntroVariant({
        hasTrialPlan,
        isPaidSpace: entitlement.status === 'unlimited',
        isWhiteLabel: IS_WHITE_LABEL,
      })
    : null;
  const { hasShown, isError, isLoading } = useClientStorageFlag(RECORDING_INTRO_STORAGE_KEY, {
    enabled: variant !== null,
  });

  if (!variant || isLoading || isError || hasShown) return null;

  return variant;
};
